// "En tu tienda": la tarjeta de un producto que ya está en Tiendanube, debajo del Estado de la ficha
// (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento).
//
// Dice qué va a cambiar en la tienda al publicar —cada cambio, con lo que hay hoy y lo que queda, es un
// atajo a su sección—, lo que la ficha tiene distinto pero no se escribe allá, y tiene las dos cosas que
// no son de ninguna sección: ver el producto en la tienda y volver a traer la ficha de allá.
//
// Las fotos nuevas cuentan como un cambio más, y las secciones trabadas se nombran: lo que no se toca
// también se dice (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas).
import { useState } from 'react';
import { ArrowRight, Check, ChevronRight, ExternalLink, Info, Loader2, Lock, RotateCcw } from 'lucide-react';
import { boton } from './botones';
import { irASeccion } from './Seccion';
import type { ComparacionConLaTienda, ProductoEnLaTienda } from './cambiosContraLaTienda';

export type LecturaDeLaTienda =
  | { estado: 'cargando' }
  | { estado: 'error' }
  /** La tienda ya no lo tiene (lo borraron allá). */
  | { estado: 'no-esta' }
  | { estado: 'listo'; publicado: ProductoEnLaTienda };

/** Un texto largo (la descripción) en una línea. */
const corto = (t: string, max = 60) => (t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t);

export function EnTuTienda({
  lectura,
  comparacion,
  trabadas = null,
  traerTraba = false,
  onReleer,
  onTraer,
}: {
  lectura: LecturaDeLaTienda;
  comparacion: ComparacionConLaTienda | null;
  /** Las secciones trabadas de la ficha, para nombrarlas. null = este producto no traba (es de antes). */
  trabadas?: Array<{ id: string; titulo: string }> | null;
  /** "Traer lo de la tienda" deja todas las secciones trabadas (el lanzamiento va sólo a Tiendanube). */
  traerTraba?: boolean;
  onReleer: () => void;
  /** Deja la ficha como la tienda. Devuelve el error, o null. */
  onTraer: () => Promise<string | null>;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [trayendo, setTrayendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cambios = comparacion?.cambios ?? [];
  // Lo que "Traer lo de la tienda" pierde: los cambios de la ficha, también los de una sección trabada.
  // Las fotos nuevas no, porque traer no las toca.
  const sePierden = cambios.filter((c) => c.seccion !== 'fotos').length + (comparacion?.trabados.length ?? 0);
  const sinMandar = comparacion?.trabados.length ?? 0;
  const url = lectura.estado === 'listo' ? lectura.publicado.url : null;

  const traer = async () => {
    setConfirmando(false);
    setTrayendo(true);
    setError(null);
    const e = await onTraer();
    setTrayendo(false);
    if (e) setError(e);
  };

  return (
    <section
      data-tour="ficha-en-tu-tienda"
      aria-label="El producto en tu tienda"
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 py-3 px-4 sm:px-5">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">En tu tienda</p>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
          >
            Ver en la tienda <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="space-y-3 p-4 sm:p-5">
        {lectura.estado === 'cargando' ? (
          <p className="flex items-center gap-2 text-xs text-neutral-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Leyendo cómo está en tu tienda…
          </p>
        ) : lectura.estado === 'error' ? (
          <div className="space-y-2">
            <p className="text-xs text-neutral-600">
              No se pudo leer cómo está en tu tienda. Sin eso no se sabe qué cambia, y al publicar no se le toca nada.
            </p>
            <button type="button" onClick={onReleer} className={boton('neutro', { chico: true })}>
              <RotateCcw className="h-3.5 w-3.5" /> Reintentar
            </button>
          </div>
        ) : lectura.estado === 'no-esta' ? (
          <p className="text-xs text-neutral-600">
            Ya no está en tu tienda: lo borraron allá. Para volver a publicarlo hay que sacarlo de este lanzamiento y
            crearlo de nuevo.
          </p>
        ) : cambios.length === 0 ? (
          <div className="flex items-start gap-3 rounded-xl bg-neutral-50 p-3">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-neutral-200 text-neutral-600">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-brand-black">Igual que en tu tienda</span>
              <span className="mt-0.5 block text-xs text-neutral-500">
                {trabadas
                  ? 'Para cambiar algo, tocá “Editar en la tienda” en su sección. Las fotos que sumes también se publican.'
                  : 'Lo que cambies acá se marca en azul y se actualiza en la tienda al publicar.'}
              </span>
            </span>
          </div>
        ) : (
          <div className="rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] p-3">
            <p className="text-sm font-semibold text-brand-primary">
              {cambios.length === 1 ? '1 cambio al publicar' : `${cambios.length} cambios al publicar`}
            </p>
            <ul className="mt-2 space-y-1">
              {cambios.map((c) => (
                <li key={c.campo}>
                  <button
                    type="button"
                    onClick={() => irASeccion(c.seccion)}
                    className="group flex w-full items-start gap-2 rounded-lg py-1.5 px-2 text-left text-xs text-brand-black transition-colors hover:bg-white/70"
                  >
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{c.etiqueta}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-neutral-500">
                        <span className="line-through decoration-neutral-400">{corto(c.antes)}</span>
                        <ArrowRight className="h-3 w-3 shrink-0" aria-label="pasa a" />
                        <span className="text-brand-black">{corto(c.ahora)}</span>
                      </span>
                    </span>
                    <ChevronRight className="mt-px h-3.5 w-3.5 shrink-0 text-[rgb(var(--brand-primary-rgb)_/_0.6)] transition-transform group-hover:translate-x-0.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {lectura.estado === 'listo' && trabadas && trabadas.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-neutral-500">
            <Lock className="mt-px h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
            <span className="min-w-0">
              <span className="text-brand-black">No se tocan en la tienda:</span>{' '}
              {trabadas.map((t, i) => (
                <span key={t.id}>
                  {i > 0 && ', '}
                  <button type="button" onClick={() => irASeccion(t.id)} className="hover:text-brand-black hover:underline">
                    {t.titulo}
                  </button>
                </span>
              ))}
              .
              {sinMandar > 0 &&
                (sinMandar === 1
                  ? ' Tenés 1 cambio en una de ellas que no se manda.'
                  : ` Tenés ${sinMandar} cambios en ellas que no se mandan.`)}
            </span>
          </div>
        )}

        {(comparacion?.noSeEscriben ?? []).map((t) => (
          <p key={t} className="flex items-start gap-2 text-xs text-neutral-500">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-neutral-400" />
            {t}
          </p>
        ))}

        {lectura.estado === 'listo' &&
          (confirmando ? (
            <div className="space-y-2 rounded-xl bg-neutral-50 p-3">
              <p className="text-xs text-neutral-700">
                {sePierden === 1
                  ? 'Se pierde el cambio que hiciste: la ficha queda como en tu tienda.'
                  : `Se pierden los ${sePierden} cambios que hiciste: la ficha queda como en tu tienda.`}
                {traerTraba ? ' Todas las secciones quedan trabadas.' : ''} Las fotos no se tocan.
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => void traer()} className={boton('primario', { chico: true })}>
                  Traer
                </button>
                <button type="button" onClick={() => setConfirmando(false)} className={boton('neutro', { chico: true })}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={trayendo}
              onClick={() => (sePierden > 0 ? setConfirmando(true) : void traer())}
              className={boton('neutro', { extra: 'w-full' })}
            >
              {trayendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 text-neutral-500" />}
              Traer lo de la tienda
            </button>
          ))}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </section>
  );
}
